// review-payload — the typed answers a reviewer stages BESIDE the thumb.
//
// A review decision asks one question: "is this true?". Four kinds ask a
// SECOND question that only a human can answer, and the answer travels with
// the thumb as one submission (ADR-0015):
//
//   - the three ATLAS call artefacts ask "which records does it apply to?"
//     (SEP-183) and answer with `assignTo`;
//   - a relationship proposal asks "who is the other person, and what is each
//     end's role?" (SEP-215) and answers with `promoteTo`;
//   - a corporate-action draft asks "are the extracted fields right?"
//     (SEP-201) and answers with `correction`;
//   - a monthly narrative asks "which variant did you pick?" and answers with
//     `selection`.
//
// The other twenty-one kinds ask nothing more, and they carry no payload.
//
// WHY THIS FILE EXISTS. The four answers used to travel as
// `extra: Record<string, unknown>` plus two named fields that had escaped onto
// the SHARED decision schema, so every kind declared two fields that one kind
// read, and the bag was typed by nothing. A key that did not match was dropped
// in silence: an approved monthly narrative discarded the chosen variant in
// production for exactly that reason. Each schema below is now a field of the
// kind's own spec (`../reviewable-kind-spec.ts`), so the wire carries the
// payload of that kind and no other, and a wrong key is a compile error and a
// 400 rather than a silent no-op.
//
// The schemas live here, and not in `../contracts/agent.ts`, because the spec
// declares them and `contracts/agent.ts` imports the spec. This file therefore
// imports nothing from either.

import { z } from 'zod';

// ---------------------------------------------------------------------------
// SEP-183 — accept and assign
// ---------------------------------------------------------------------------

/**
 * The record tiers an accepted knowledge insight can be assigned to
 * (SEP-183 / ADR-0015). These are Object Type KEYS, pinned verbatim.
 *
 * NOTE the tiers: `account` is the registration grain, and `client` is the
 * registered holder above it. They are different rows, and a reader must not
 * read one label for the other.
 */
export const knowledgeAssignmentTargetTypeSchema = z.enum(['suitability-record', 'account']);
export type KnowledgeAssignmentTargetType = z.infer<typeof knowledgeAssignmentTargetTypeSchema>;

export const knowledgeAssignmentTargetSchema = z.object({
    targetType: knowledgeAssignmentTargetTypeSchema,
    targetId: z.string(),
});
export type KnowledgeAssignmentTarget = z.infer<typeof knowledgeAssignmentTargetSchema>;

/**
 * The payload of the three ATLAS call artefacts.
 *
 * An empty list means the insight stays at Contact level and assigns nothing,
 * which is the ADR-0015 default. The field is therefore optional, and an
 * absent field and an empty list mean the same thing.
 */
export const callArtefactPayloadSchema = z
    .object({
        assignTo: z.array(knowledgeAssignmentTargetSchema).optional(),
    })
    .strict();
export type CallArtefactPayload = z.infer<typeof callArtefactPayloadSchema>;

// ---------------------------------------------------------------------------
// SEP-215 — approve and promote
// ---------------------------------------------------------------------------

/**
 * The counterparty of a promoted relationship proposal (SEP-215 / ADR-0015).
 *
 * A proposal names a person; a name binds nothing
 * (`apps/server/src/lib/desk/match-contact.ts` states the rule: a telephone
 * number is evidence, a matching surname is not). The reviewer therefore makes
 * one of two explicit choices, and the server never guesses between them.
 */
export const relationshipCounterpartySchema = z.discriminatedUnion('kind', [
    /** The reviewer picked a Contact that already exists in their book. */
    z.object({ kind: z.literal('existing-contact'), contactId: z.string().min(1) }),
    /** The reviewer chose "create new". The server creates a draft Contact
     *  under the reviewer's own book, then links it. */
    z.object({
        kind: z.literal('new-draft-contact'),
        firstName: z.string().min(1),
        lastName: z.string().min(1),
    }),
]);
export type RelationshipCounterparty = z.infer<typeof relationshipCounterpartySchema>;

/**
 * The reviewer's answer to "who is this person, and how are the two related?".
 *
 * Both roles are RelationshipRole catalogue keys with
 * `appliesTo: 'contact_contact'`. The Action validates them (`UNKNOWN_ROLE`).
 */
export const relationshipPromotionSchema = z.object({
    counterparty: relationshipCounterpartySchema,
    /** The role that the SUBJECT's end of the relationship carries. */
    subjectRole: z.string().min(1),
    /** The role that the COUNTERPARTY's end carries. It defaults to the
     *  proposed role, which is the right answer for a symmetric pair. */
    counterpartyRole: z.string().min(1).optional(),
    startDate: z.string().date().optional(),
});
export type RelationshipPromotion = z.infer<typeof relationshipPromotionSchema>;

/**
 * The payload of a relationship proposal.
 *
 * An absent `promoteTo` means the proposal is approved but no `Relationship`
 * is written, which is the safe default when the reviewer cannot name the
 * counterparty yet.
 */
export const relationshipProposalPayloadSchema = z
    .object({
        promoteTo: relationshipPromotionSchema.optional(),
    })
    .strict();
export type RelationshipProposalPayload = z.infer<typeof relationshipProposalPayloadSchema>;

// ---------------------------------------------------------------------------
// SEP-201 — the corporate-action draft correction
// ---------------------------------------------------------------------------

/**
 * The nine fields an Ops reviewer can correct on a `CoacActionDraft` before
 * the approval materialises the live CoacAction (SEP-201 / ADR-0020).
 *
 * EVERY KEY IS THE PROPERTY NAME THE OBJECT TYPE DECLARES. That is the whole
 * point of the type: the server patch copies these names onto the row, so a
 * name that does not match is a compile error here and not a dropped field at
 * run time.
 *
 * The four dates travel as the VERBATIM TEXT of the notice, because CEDAR
 * converts nothing and the server does the reading. `null` clears the field.
 */
export const coacDraftCorrectionSchema = z
    .object({
        securityName: z.string().optional(),
        eventType: z.string().optional(),
        responseDeadline: z.string().nullable().optional(),
        payDate: z.string().nullable().optional(),
        recordDate: z.string().nullable().optional(),
        exDate: z.string().nullable().optional(),
        wealthManager: z.string().nullable().optional(),
        /**
         * The election options that Ops confirms before the approval routes the
         * corporate action to the Desk (SEP-251 request 8).
         *
         * The reviewer gives the WHOLE list, because a correction of one option
         * is a correction of the set that the custodian recognises. The three
         * states of the field stay apart, the same as on the row:
         *
         * - absent: the reviewer corrected nothing, and the stored list stands.
         * - `null`: the reviewer cleared the list.
         * - `[]`: the notice prints no election option.
         *
         * The server refuses an unusable list BEFORE the review flip, with the
         * rules that `vendor/septima-ontology-wealth/packages/ontology-wealth/src/coac/election-option.ts`
         * holds. The shape is restated here, and not imported, because the
         * contract must not depend on the ontology runtime (ADR-0003).
         */
        electionOptions: z
            .array(
                z.object({
                    code: z.string(),
                    label: z.string(),
                    isDefault: z.boolean(),
                }),
            )
            .nullable()
            .optional(),
        /** What the reviewer changed, and why. Recorded on the draft row. */
        correctionNote: z.string().optional(),
    })
    .strict();
export type CoacDraftCorrection = z.infer<typeof coacDraftCorrectionSchema>;

export const coacActionDraftPayloadSchema = z
    .object({
        correction: coacDraftCorrectionSchema.optional(),
    })
    .strict();
export type CoacActionDraftPayload = z.infer<typeof coacActionDraftPayloadSchema>;

// ---------------------------------------------------------------------------
// The monthly-narrative variant selection
// ---------------------------------------------------------------------------

/**
 * The variant the Investment Manager picked, and the two body fields that
 * mirror it.
 *
 * EVERY KEY IS THE PROPERTY NAME THE OBJECT TYPE DECLARES, for the same reason
 * as the corporate-action correction above. This is the exact shape that broke
 * in production: the emitted keys and the server guard were both snake_case
 * while the Object Type declares camelCase, so the patch was always empty and
 * every approval kept the default variant, silently.
 */
export const monthlyNarrativeSelectionSchema = z
    .object({
        selectedVariantIndex: z.number().int().min(0),
        performanceSummary: z.string(),
        narrativeParagraph: z.string(),
    })
    .strict();
export type MonthlyNarrativeSelection = z.infer<typeof monthlyNarrativeSelectionSchema>;

export const monthlyNarrativeDraftPayloadSchema = z
    .object({
        selection: monthlyNarrativeSelectionSchema.optional(),
    })
    .strict();
export type MonthlyNarrativeDraftPayload = z.infer<typeof monthlyNarrativeDraftPayloadSchema>;
